/**
 * 会话门（学员端页面渲染前的强制前置）
 * -----------------------------------------------------------------------------
 * 口径 C-01：任意页面渲染前必须先确认会话，防止转发链接泄漏数据。
 * 未授权 → **不渲染任何业务内容**（不是「渲染了再遮住」）。
 *
 * 未授权时去哪，取决于学员**是不是从本系统的渠道入口进来的**：
 *
 *   1. **带入口码**（URL `?entry=`，或会话存储里已有）→ 说明来自公众号菜单 / 海报 /
 *      投放短链，是本系统的正式入口。此时**直接静默授权**（`snsapi_base` 无感知、
 *      不弹确认框），学员感知是「点菜单 → 一闪 → 服务台」。
 *      若仍先落引导页，已经关注过的人会被要求「请先关注公众号再报名」并多点一次 ——
 *      文案错位，且每次从菜单进来都要重复一遍。微信菜单跳转网页**不带任何身份**，
 *      所以「从菜单进」并不等于「带会话」，这跳一次省不掉，但可以做到无感。
 *
 *   2. **不带入口码**（转发链接、书签、直接输入地址）→ 落引导页给出关注引导。
 *      这类来源本就不该直接拿到业务数据（C-01 的初衷）。
 *
 * 防循环：授权是整页跳出去再回来。若回来仍未拿到会话（用户拒绝 / code 失效 /
 * 服务端换码失败），会再次 401 而再次跳授权，形成无限循环。因此两道闸：
 *   - `kickedRef`：同一次挂载内只处理一次（同步，不受存储可用性影响）
 *   - `enroll.authAttempt`：会话级标记，保证**每个会话生命周期最多自动授权一次**，
 *     之后一律落引导页。会话恢复时清掉，下次过期仍可无感续期。
 *
 * 转发场景（`AUTH-010`）**不自动授权** —— 那种情况要的是「请从公众号菜单进入」的提示，
 * 自动跳授权会把这条提示语吃掉。
 *
 * 任务卡：T-03（认证与会话）/ T-27（公众号真实接入）
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, resolveEntryCode } from '../api/client';
import { getAuthorizeUrl } from '../api/h5';
import { useSession } from '../api/hooks';
import { ErrorBlock, LoadingBlock } from './layout';

/** 会话级「本会话已自动授权过一次」标记，用于打断授权失败后的死循环 */
const AUTH_ATTEMPT_KEY = 'enroll.authAttempt';

function readAttempted(): boolean {
  try {
    return window.sessionStorage.getItem(AUTH_ATTEMPT_KEY) === '1';
  } catch {
    // 隐私模式下 sessionStorage 不可读：保守当成「已尝试过」，宁可落引导页也不循环跳转
    return true;
  }
}

function markAttempted(): void {
  try {
    window.sessionStorage.setItem(AUTH_ATTEMPT_KEY, '1');
  } catch {
    // 写不进去最坏只是多跳一轮，`kickedRef` 仍能兜住同一次挂载
  }
}

function clearAttempted(): void {
  try {
    window.sessionStorage.removeItem(AUTH_ATTEMPT_KEY);
  } catch {
    // 忽略：标记残留只会让下次过期少一次无感续期，不影响正确性
  }
}

/** 入口码：URL 上的优先（刚落地时一定在），其次取会话存储（SPA 内跳转 / 授权往返会丢查询串） */
function currentEntry(search: string): string | null {
  return new URLSearchParams(search).get('entry') ?? resolveEntryCode();
}

export function RequireSession({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isPending, error, refetch } = useSession();

  const sessionExpired = error instanceof ApiError && error.isSessionError;
  /** 转发场景：要「请从公众号菜单进入」的提示，不是缺会话 */
  const shareEntry = error instanceof ApiError && error.code === 'AUTH-010';
  const genericError = error instanceof ApiError && !error.isSessionError;

  /**
   * 是否该自动发起静默授权。**在渲染期就算出来**，而不是等 effect 跑完再置状态 ——
   * 否则用户会先看到「正在前往引导页」再跳成「正在进入」，在慢网络下这个错位很显眼。
   * 这里读 sessionStorage 是同步只读，不构成副作用。
   */
  const willAuthorize =
    sessionExpired && !shareEntry && !readAttempted() && Boolean(currentEntry(location.search));

  /** 授权已发起。与 `willAuthorize` 配合：`kickedRef` 置位后前者可能转 false，靠它保持文案稳定 */
  const [authorizing, setAuthorizing] = useState(false);
  const kickedRef = useRef(false);

  // 会话恢复 → 清标记，让下一次过期仍可无感续期
  useEffect(() => {
    if (data) clearAttempted();
  }, [data]);

  useEffect(() => {
    if (!sessionExpired || kickedRef.current) return;
    kickedRef.current = true;

    /**
     * 与上面 `willAuthorize` 同构的判断，此处重算一遍而不是复用变量：
     * `willAuthorize` 依赖 `sessionStorage`（非响应式），放进依赖数组会让 effect
     * 的触发时机变得不可推理。两处条件必须保持一致。
     */
    if (shareEntry || readAttempted()) {
      navigate('/guide', { replace: true });
      return;
    }

    const entry = currentEntry(location.search);
    if (!entry) {
      navigate('/guide', { replace: true });
      return;
    }

    markAttempted();
    setAuthorizing(true);
    getAuthorizeUrl(entry)
      .then(({ url }) => {
        // 必须整页跳转：授权发生在 open.weixin.qq.com 域内，只有顶层导航才能把 code 带回来
        window.location.assign(url);
      })
      .catch(() => {
        // 拿不到授权地址（服务端不可用 / H5_BASE_URL 未配置）→ 落引导页给可执行动作
        setAuthorizing(false);
        navigate('/guide', { replace: true });
      });
  }, [sessionExpired, shareEntry, location.search, navigate]);

  if (authorizing || willAuthorize) return <LoadingBlock text="正在进入" />;
  if (isPending) return <LoadingBlock text="正在确认身份" />;
  if (sessionExpired) return <LoadingBlock text="正在前往引导页" />;
  if (genericError || !data) {
    return (
      <ErrorBlock
        message={error instanceof Error ? error.message : '无法确认身份，请稍后重试'}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  return children;
}
